import React, { useState } from 'react';

import Avatar, { AvatarType, Size } from '@rover/components/src/Avatar/Avatar';
import CalloutBadge from '@rover/components/src/callouts/CalloutBadge/CalloutBadge';
import Modal from '@rover/components/src/OverflowTextModal/Modal';
import EXPERIMENTS from '@rover/constants/src/platform/experiments.constants';
import { Box, Button, Flex, Heading, Paragraph } from '@rover/kibble/core';
import { HeadingSizes, Spacing } from '@rover/kibble/tokens/build/rover/es6/theme';
import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategories } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import type { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import type { PersonBadge } from '@rover/rsdk/src/apiClient/latest/model/personBadge';
import type { PersonTrainerProfileFlair } from '@rover/rsdk/src/apiClient/latest/model/personTrainerProfileFlair';
import type { TrainerInsights } from '@rover/rsdk/src/apiClient/latest/model/trainerInsights';
import {
  FLAGS,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import useExperimentsShim from '@rover/shared/js/hooks/useExperimentsShim';

import { TrainerFlair } from '../../../components/TrainerFlair';
import VirtualOnlyBadge from '../../../components/VirtualOnlyBadge';
import RatingSummary from '../RatingSummary/RatingSummary';
import { RepeatClientSummary } from '../RepeatClientSummary';

type TrainerProps = {
  badges: PersonBadge[];
  location: string;
  defaultImage: Image;
  shortName: string;
  variant?: 'compact' | 'full';
  justifyContent?: string;
  ownProfile?: boolean;
  isApproved?: boolean;
  onAvatarClick?: (index: number) => void;
  inTrainingPilot: boolean;
  opk: string;
  virtualOnly: boolean;
  trainerInsights?: TrainerInsights;
  flair?: PersonTrainerProfileFlair;
};

const BadgeIcon = (icon: string, title: string): JSX.Element => (
  <img src={icon} alt={title} width="100%" />
);

type AlternateViewParagraphProps = {
  isApproved: boolean;
  ownProfile?: boolean;
  variant: 'compact' | 'full';
  shortName: string;
  inTrainingPilot: boolean;
};

const AlternateViewParagraph = ({
  isApproved,
  ownProfile,
  variant,
  shortName,
  inTrainingPilot,
}: AlternateViewParagraphProps): JSX.Element | null => {
  let text: string | null = null;

  if (!isApproved && !ownProfile) {
    //  TODO (DEV-104195): Translate below text after training pilot launch
    text = `This profile has not been completed and reviewed by Rover's team. Once it has been approved, pet parents will be able to contact ${shortName}`;
  } else if (!isApproved && ownProfile) {
    //  TODO (DEV-104195): Translate below text after training pilot launch
    text =
      "Your profile has not been completed and reviewed by Rover's team. Once it has been approved, dog owners will be able to contact you.";
  } else if (isApproved && ownProfile) {
    //  TODO (DEV-104195): Translate below text after training pilot launch
    text =
      'Your profile has been approved. Pet parents viewing your profile can now send you booking requests.';
  } else if (!inTrainingPilot) {
    //  TODO (DEV-104195): Translate below text after training pilot launch
    text = "Rover's training marketplace is currently limited to select locations.";
  }

  if (text === null || variant !== 'full') {
    return null;
  }
  return (
    <Paragraph size="200" pt="4x" paddingBottom="0x" marginBottom="0x">
      {text}
    </Paragraph>
  );
};

const TrainerInformation = ({
  badges,
  location,
  defaultImage,
  shortName,
  variant = 'full',
  justifyContent = 'center',
  ownProfile,
  isApproved = true,
  onAvatarClick,
  inTrainingPilot,
  opk,
  virtualOnly,
  trainerInsights,
  flair,
}: TrainerProps): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { isFlagActive } = useFrontendConfiguration();
  const { isVariant } = useExperimentsShim();

  const isInRepeatTrainingClientCountVariant = isVariant(
    EXPERIMENTS.REPEAT_TRAINING_CLIENT_COUNT_EXPERIMENT
  );

  const isAllServicesClientCountActive = isFlagActive(
    FLAGS.ROLLOUT_TRAINING_ALL_SERVICE_CLIENT_COUNT
  );

  const repeatClientCount = isAllServicesClientCountActive
    ? trainerInsights?.allServiceRepeatClientCount
    : trainerInsights?.repeatTrainingClientCount;

  const openModal = ({ webEvent }: { webEvent?: React.MouseEvent<HTMLElement> }): void => {
    webEvent?.stopPropagation();
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
  };

  const BADGE_SIZE = variant === 'compact' ? '24px' : ['24px', '32px'];
  const badge = badges[0];
  const hasReviews =
    typeof trainerInsights?.allServiceReviewsCount === 'number' &&
    trainerInsights?.allServiceReviewsCount > 0;
  const hideReviewCountLink = !trainerInsights?.servicesWithTextReviews?.length;
  const hasTrainingReviews = trainerInsights?.servicesWithTextReviews.some(
    (service) => service.slug === 'dog-training'
  );
  const defaultCategory = hasTrainingReviews
    ? ServiceTypeCategories['dog-training']
    : ServiceTypeCategories['all-services'];

  const renderAvatar = (size: string): JSX.Element => (
    <Box
      position="relative"
      width={size}
      mr="4x"
      sx={{ cursor: onAvatarClick ? 'pointer' : 'default' }}
      onClick={onAvatarClick ? () => onAvatarClick(0) : undefined}
      data-testid="trainer-avatar"
    >
      <Avatar
        diameter={size}
        size={Size.Profile}
        borderRadius="50%"
        avatarType={AvatarType.Person}
        imageUrl={defaultImage.medium}
      />
      {badge && (
        <Flex
          alignItems="bottom"
          position="absolute"
          width={BADGE_SIZE}
          height={BADGE_SIZE}
          right="0"
          bottom="0"
        >
          {variant === 'full' ? (
            <Button
              icon={() => BadgeIcon(badge.icon, badge.title)}
              circular
              aria-label={badge.title}
              onPress={openModal}
              m="0x"
              p="0x"
              sx={{
                backgroundColor: 'transparent',
                '&:hover': { background: 'none' },
                '&:focus': { background: 'none' },
                border: 0,
              }}
              data-testid="badge-icon-button"
            />
          ) : (
            BadgeIcon(badge.icon, badge.title)
          )}
        </Flex>
      )}
    </Box>
  );

  const styles = {
    compact: {
      fontSize: '400' as HeadingSizes,
      avatarDiameter: '80px',
      direction: 'row',
      alignItems: 'center',
      minHeight: '0px',
      badgeDirection: ['initial', 'column', 'row'],
      badgeAlignment: ['initial', 'start', 'center'],
      sitterNameMt: '0x' as Spacing,
      badgeSpacing: {
        pl: ['0x', '0x', '2x'],
        mt: ['1x', '2x', '1x'],
      },
      gap: '0x',
    },
    full: {
      fontSize: '500' as HeadingSizes,
      avatarDiameter: '112px',
      direction: ['row', 'column'],
      alignItems: 'start',
      minHeight: '24px',
      badgeDirection: 'column',
      badgeAlignment: 'initial',
      sitterNameMt: '1x' as Spacing,
      badgeSpacing: {
        pl: undefined,
        mt: ['0x', '1x'],
      },
      gap: '1x',
    },
  };

  const showRepeatClients =
    (isInRepeatTrainingClientCountVariant || isAllServicesClientCountActive) &&
    ![undefined, 0].includes(repeatClientCount);

  return (
    <>
      <Flex
        flexDirection={styles[variant].direction}
        justifyContent={justifyContent}
        alignItems={styles[variant].alignItems}
        data-testid="trainer-information"
      >
        <Box display={['block', 'none']} minWidth="80px">
          {renderAvatar('64px')}
        </Box>
        <Box display={['none', 'block']}>{renderAvatar(styles[variant].avatarDiameter)}</Box>
        <Flex flexDirection="column" gap={styles[variant].gap}>
          <Flex
            flexDirection={styles[variant].badgeDirection}
            alignItems={styles[variant].badgeAlignment}
          >
            <Box display={variant === 'full' ? ['none', 'block'] : 'block'}>
              <Heading
                mt={styles[variant].sitterNameMt}
                size={styles[variant].fontSize}
                webProps={{ responsive: false }}
                mb="0x"
                sx={{ fontFamily: 'Bogart-Semibold' }}
              >
                {shortName}
              </Heading>
            </Box>
            <Box display={variant === 'full' ? ['block', 'none'] : 'none'}>
              <Heading mt="0x" size="300" webProps={{ responsive: false }} mb="0x">
                {shortName}
              </Heading>
            </Box>
            <Box sx={{ ...styles[variant].badgeSpacing }}>
              {!isApproved && (
                <CalloutBadge
                  value="Coming Soon"
                  calloutBadgeStyle={CalloutBadge.Styles.COMING_SOON}
                />
              )}
            </Box>
          </Flex>
          {flair && <TrainerFlair slug={flair.slug} displayText={flair.displayText} />}
          <Paragraph size="200" mt={['0x', '1x']} mb={['0x', '1x']} textColor="secondary">
            {virtualOnly ? <VirtualOnlyBadge /> : location}
          </Paragraph>
          {hasReviews && (
            <RatingSummary
              ratingsAverage={trainerInsights?.allServiceAverageRating}
              reviewsCount={trainerInsights?.allServiceReviewsCount}
              displayFor="compact"
              providerOpk={opk}
              hideReviewCountLink={hideReviewCountLink}
              defaultCategory={defaultCategory}
            />
          )}
          {showRepeatClients && (
            <RepeatClientSummary repeatClientCount={repeatClientCount} displayFor="compact" />
          )}
        </Flex>
        {hasReviews && (
          <RatingSummary
            ratingsAverage={trainerInsights?.allServiceAverageRating}
            reviewsCount={trainerInsights?.allServiceReviewsCount}
            displayFor="full"
            providerOpk={opk}
            hideReviewCountLink={hideReviewCountLink}
            defaultCategory={defaultCategory}
          />
        )}
        {showRepeatClients && (
          <RepeatClientSummary repeatClientCount={repeatClientCount} displayFor="full" />
        )}
      </Flex>
      {badge && (
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <Box width="100%" data-testid="trainer-badge-modal">
            <Heading size="300">{badge.title}</Heading>
            {/* @ts-expect-error */}
            <Paragraph size="200" mb="8x" dangerouslySetInnerHTML={{ __html: badge.description }} />
          </Box>
        </Modal>
      )}
      <AlternateViewParagraph
        isApproved={isApproved}
        ownProfile={ownProfile}
        variant={variant}
        shortName={shortName}
        inTrainingPilot={inTrainingPilot}
      />
    </>
  );
};

export default TrainerInformation;
